import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Helmet } from 'react-helmet';
import { Row, Col } from 'antd';
import { keyBy, find, get, has, max, cloneDeep, size } from 'lodash';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import helpers from 'utils/helpers';
import currency from 'utils/currency';
import {
  WrapperDiv,
  ShipmentRow,
  ChargeRow,
  ItemsRow,
  StyledButton,
  ResponsiveDesktopDiv,
  ResponsiveMobileDiv
} from 'components/Styled';
import InfoBar from 'components/common/InfoBar';
import CheckpointsTimeline from './CheckpointsTimeline';
import AddressCard from './AddressCard';
import ExpandButton from './ExpandButton';
import ImagePlane from 'assets/images/plane-black.svg';
import { getTransactionsRequest } from 'ducks/users';
import { getCampaignUserInfoRequest } from 'ducks/campaigns';
import { duplicateShipmentRequest } from 'ducks/shipments';
import Hints from 'assets/images/hints.svg';

import countries from 'data/countries';
import categories from 'data/categories';

const keyedCategories = keyBy(categories, 'id');

class TransactionDetails extends Component {
  constructor(props) {
    super(props);
    this.pricing = React.createRef();
    this.state = {
      uiLoaded: false
    };
  }

  authentication = () => {
    if (this.props.user) {
      if (
        !this.props.transactions ||
        (this.props.location.state !== undefined &&
          this.props.location.state.reschedulePickupSuccess)
      ) {
        this.props.getTransactionsRequest({ user: this.props.user });
      } else {
        this.setState({
          uiLoaded: true
        });
      }
    } else if (this.props.user === false) {
      this.props.history.push(`${process.env.PUBLIC_URL}/login`);
    }
  };

  handleOriginExpand = () => {
    this.setState({
      originExpand: !this.state.originExpand
    });
  };

  handleDestinationExpand = () => {
    this.setState({
      destinationExpand: !this.state.destinationExpand
    });
  };

  handleDuplicateClick = () => {
    const transaction = find(this.props.transactions, {
      ref: this.props.match.params.ref
    });

    const shipment = get(transaction, 'shipments[0]') || {};

    this.props.duplicateShipmentRequest({
      shipmentRef: shipment.ref
    });
  };

  handlePriceing = transaction => {
    if (transaction) {
      const shipment = transaction?.shipments?.[0] || {};
      const finalTotal = transaction?.finalTotal || 0;
      const totalCharges = transaction?.totalCharges || 0;
      const {
        totalRate,
        freightCharge,
        fuelSurcharge,
        otherCharges
      } = shipment;

      const discount = transaction?.discount || 0;
      const pickupFee = transaction?.pickup?.pickupFee || 0;

      this.pricing = {
        HKD: new currency(
          transaction?.naturalAmount?.finalTotal ||
            transaction?.naturalAmount?.totalCharges ||
            finalTotal ||
            totalCharges
        ),
        discount: new currency(discount),
        pickupFee: new currency(pickupFee),
        finalTotal: new currency(finalTotal),
        totalCharges: new currency(totalCharges),
        totalRate: new currency(totalRate),
        freightCharge: new currency(freightCharge || 0),
        fuelSurcharge: new currency(fuelSurcharge || 0),
        otherCharges: new currency(otherCharges || 0),
        chargedRate: new currency(totalRate).add(pickupFee),
        adjustment: new currency(totalCharges)
          .add(discount)
          .subtract(pickupFee)
          .subtract(totalRate)
      };
    }
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.user !== this.props.user) {
      this.authentication();
    }

    if (prevProps.transactions !== this.props.transactions) {
      const transaction = find(this.props.transactions, {
        ref: this.props.match.params.ref
      });
      const trackingNumber = get(transaction, 'shipments[0].trackingNumber');
      window.dataLayer.push({
        event: 'Land Shipment Tracking Page',
        trackingNumber
      });
      this.props.getCampaignUserInfoRequest({ orderRef: transaction?.ref });
      this.handlePriceing(transaction);
    }

    if (prevProps.uiLoading && !this.props.uiLoading) {
      this.setState({
        uiLoaded: true
      });
    }
    if (
      !prevProps.duplicateShipmentSuccess &&
      this.props.duplicateShipmentSuccess
    ) {
      helpers.message.success(
        window.t('error_code.duplicate-shipment-success')
      );

      this.props.history.push(
        `${process.env.PUBLIC_URL}/create-shipment/quotation`
      );

      return;
    }
  }

  componentDidMount() {
    this.authentication();
    if (this.props.transactions) {
      const transaction = find(this.props.transactions, {
        ref: this.props.match.params.ref
      });
      this.props.getCampaignUserInfoRequest({ orderRef: transaction?.ref });
      this.handlePriceing(transaction);
    }
    if (
      this.props.location.state !== undefined &&
      this.props.location.state.reschedulePickupSuccess
    ) {
      helpers.message.success(
        this.props.t('reschedule_pickup.reschedule_success'),
        'auth'
      );
      return;
    }
  }

  render() {
    const countriesData = keyBy(this.props.countries, 'alpha-2');

    let transaction = null;
    if (this.props.data) {
      transaction = cloneDeep(this.props.data);
    } else {
      transaction = find(this.props.transactions, {
        ref: this.props.match.params.ref
      });
    }

    const shipment = get(transaction, 'shipments[0]') || {};
    const pickup = get(transaction, 'pickup') || {};

    shipment.finalTotalRate = shipment.finalTotalRate || shipment.totalRate;

    if (!transaction && this.state.uiLoaded) {
      this.props.history.push(`${process.env.PUBLIC_URL}/account/orders`);

      return null;
    }

    if (this.props.campaignUserExist === null) return null;

    return (
      <React.Fragment>
        <Helmet>
          <title>
            {`${this.props.t('transactions.my_items')}`}
            {shipment.trackingNumber
              ? `(TRK# ${shipment.trackingNumber})`
              : ''}{' '}
            | Spaceship
          </title>
        </Helmet>
        {transaction ? (
          <React.Fragment>
            <div
              className="trsansaction-card-container"
              style={{
                backgroundColor: '#f5f5f5',
                margin: '0 auto',
                width: '100%',
                height: '100%',
                maxWidth: `${process.env.REACT_APP_MAX_WIDTH}px`
              }}>
              <div
                style={{
                  paddingBottom: '60px',
                  display: this.state.uiLoaded ? 'block' : 'none'
                }}>
                <div
                  style={{
                    padding: '20px 0',
                    textAlign: 'center',
                    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.2)',
                    backgroundColor: '#fff',
                    position: 'relative'
                  }}>
                  <div
                    className="back-button"
                    style={{
                      fontSize: '12px',
                      position: 'absolute',
                      top: '0px',
                      left: '0px',
                      padding: '20px 15px',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      this.props.history.push(
                        `${process.env.PUBLIC_URL}/account/orders`
                      );
                    }}>
                    <FontAwesomeIcon icon="angle-left" />
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    <div
                      style={{ display: 'inline-block', paddingRight: '10px' }}>
                      {helpers.responsiveImage(
                        helpers.getCarrierImage(
                          shipment.carrierService.carrier
                        ),
                        helpers.getCarrierText(shipment.carrierService.carrier),
                        helpers.getCarrierImageWidth(
                          shipment.carrierService.carrier,
                          0.8
                        )
                      )}
                    </div>
                    <span style={{ verticalAlign: 'middle' }}>
                      {get(shipment.carrierService.name, this.props.t('key'))}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: 700,
                      padding: '7px 0 5px'
                    }}>
                    {get(
                      countriesData[shipment.fromAddress.country],
                      `name.${this.props.t('key')}`
                    )}
                    <img
                      src={ImagePlane}
                      style={{
                        width: '31.3px',
                        paddingBottom: '2px',
                        margin: '0 6px'
                      }}
                      alt=""
                    />
                    {get(
                      countriesData[shipment.toAddress.country],
                      `name.${this.props.t('key')}`
                    )}
                  </div>
                  <br />
                  <div>
                    <div style={{ fontSize: '12px' }}>
                      {this.props.t('finish.tracking_link')}
                      <a
                        style={{ color: '#9900A5' }}
                        href={helpers.getCarrierTrackingUrl(
                          shipment.carrierService.carrier,
                          shipment.trackingNumber
                        )}
                        className="buttonTheme-color"
                        target="_blank"
                        rel="noopener noreferrer">
                        {shipment.trackingNumber}{' '}
                        <FontAwesomeIcon icon="angle-right" />
                      </a>
                    </div>
                  </div>
                </div>
                <div style={{ paddingTop: '6px' }}>
                  {shipment.tracking || shipment.status === -1 ? (
                    <div style={{ padding: '40px 15px 10px' }}>
                      <CheckpointsTimeline
                        t={this.props.t}
                        status={shipment.status}
                        lastUpdate={shipment.lastUpdate}
                        checkpoints={
                          has(shipment, 'tracking.checkpoints')
                            ? shipment.tracking.checkpoints
                            : []
                        }
                      />
                    </div>
                  ) : null}
                </div>
                <div style={{ backgroundColor: '#fff' }}>
                  <WrapperDiv style={{ padding: '20px 15px 30px' }}>
                    <div
                      style={{
                        paddingBottom: '15px',
                        borderBottom: '1px solid #ccc',
                        fontSize: '20px',
                        fontWeight: 700
                      }}>
                      {this.props.t('payment.booking')}
                    </div>
                    <div style={{ paddingTop: '20px' }}>
                      {get(pickup, 'type') === 'NORMAL' && (
                        <ShipmentRow type="flex" align="top">
                          <Col span={10}>
                            {get(
                              countriesData[shipment.fromAddress.country],
                              `name.${this.props.t('key')}`
                            )}
                            {this.props.t('payment.pickup_time')}
                          </Col>
                          <Col span={14} style={{ textAlign: 'left' }}>
                            {moment(pickup.minDatetime)
                              .utcOffset(
                                process.env.REACT_APP_SERVER_UTC_OFFSET
                              )
                              .format('D MMM')}{' '}
                            (
                            {moment(pickup.minDatetime)
                              .utcOffset(
                                process.env.REACT_APP_SERVER_UTC_OFFSET
                              )
                              .format('h:mm a')}{' '}
                            -{' '}
                            {moment(pickup.maxDatetime)
                              .utcOffset(
                                process.env.REACT_APP_SERVER_UTC_OFFSET
                              )
                              .format('h:mm a')}
                            )
                          </Col>
                        </ShipmentRow>
                      )}
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          {this.props.t('payment.pickup_number')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          {get(pickup, 'pickupNumber') || 'N/A'}{' '}
                          {get(pickup, 'type') === 'NORMAL' &&
                            get(shipment, 'carrierService.csUrl') && (
                              <a
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#9900A5' }}
                                className="buttonTheme-color"
                                href={get(shipment, 'carrierService.csUrl')}>
                                {this.props.t('transactions.pickupHotline', {
                                  carrier: get(
                                    shipment,
                                    'carrierService.carrierName.en'
                                  )
                                })}{' '}
                                <FontAwesomeIcon icon="external-link-alt" />
                              </a>
                            )}
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          <ExpandButton
                            expand={this.state.originExpand}
                            onClick={this.handleOriginExpand}
                          />
                          {this.props.t(
                            `payment.${
                              get(pickup, 'type') === 'SELF_PICKUP'
                                ? 'post'
                                : 'pickup'
                            }_address`
                          )}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          <AddressCard
                            t={this.props.t}
                            country={shipment.fromAddress.country}
                            address={shipment.fromAddress}
                            expand={this.state.originExpand}
                          />
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          <ExpandButton
                            expand={this.state.destinationExpand}
                            onClick={this.handleDestinationExpand}
                          />
                          {this.props.t('payment.receiver_address')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          <AddressCard
                            t={this.props.t}
                            country={shipment.toAddress.country}
                            address={shipment.toAddress}
                            expand={this.state.destinationExpand}
                          />
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          {this.props.t('payment.expected_arrived_date')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          {(() => {
                            const {
                              deliveryDate,
                              minDeliveryDays,
                              maxDeliveryDays
                            } = shipment;

                            if (deliveryDate) {
                              return moment
                                .tz(
                                  deliveryDate,
                                  get(
                                    countriesData[shipment.toAddress.country],
                                    'zone-name'
                                  )
                                )
                                .format('D MMM h:mm a z');
                            }

                            if (minDeliveryDays && maxDeliveryDays) {
                              if (minDeliveryDays === maxDeliveryDays) {
                                return `${minDeliveryDays} ${this.props.t(
                                  'common.working_day'
                                )}`;
                              }

                              return `${minDeliveryDays} - ${maxDeliveryDays} ${this.props.t(
                                'common.working_day'
                              )}`;
                            }

                            return this.props.t('common.cant_provide');
                          })()}
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          {this.props.t('payment.total_quantity')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          {this.props.t('payment.box', {
                            amount: shipment.totalBox
                          })}
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          {this.props.t('payment.estimated_total_weight')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          {shipment.totalWeight} kg
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          {this.props.t('payment.estimated_weight')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          {shipment.totalVolumeWeight} kg
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          {this.props.t('payment.estimated_pay_weight')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          {max([
                            shipment.totalWeight,
                            shipment.totalVolumeWeight
                          ])}{' '}
                          kg
                        </Col>
                      </ShipmentRow>
                      <ShipmentRow type="flex" align="top">
                        <Col span={10}>
                          {this.props.t('payment.received_order_at')}
                        </Col>
                        <Col span={14} style={{ textAlign: 'left' }}>
                          {moment(transaction.createdAt).format('D MMM h:mm a')}
                        </Col>
                      </ShipmentRow>

                      {this.props.campaignUserExist && (
                        <ShipmentRow type="flex" align="top">
                          <Col span={10}>
                            {this.props.t('payment.campaign.partner')}
                          </Col>
                          <Col span={14} style={{ textAlign: 'left' }}>
                            {
                              this.props.campaignInfo.campaignName[
                                this.props.t('key')
                              ]
                            }
                          </Col>
                          <Col span={10}>{}</Col>
                          <Col span={14} style={{ textAlign: 'left' }}>
                            {this.props.campaignInfo.firstName +
                              ' ' +
                              this.props.campaignInfo.lastName}
                          </Col>
                          <Col span={10}>{}</Col>
                          <Col span={14} style={{ textAlign: 'left' }}>
                            {this.props.campaignInfo.memberRef}
                          </Col>
                        </ShipmentRow>
                      )}
                    </div>
                    {shipment.toAddress.country !== 'HK' &&
                      pickup.type === 'SELF_PICKUP' && (
                        <>
                          <br />
                          <br />
                          <div
                            style={{
                              color: '#2C2C2E',
                              fontSize: '12px',
                              borderTop: '1px solid #ccc',
                              paddingTop: 5,
                              textAlign: 'left'
                            }}>
                            <InfoBar
                              containerStyle={{
                                textAlign: 'left',
                                backgroundColor: 'white',
                                marginLeft: -15,
                                marginRight: -15
                              }}
                              title={{
                                __html: this.props.t(
                                  'finish.drop_off.hints.title'
                                )
                              }}
                              content={{
                                __html: this.props.t(
                                  'finish.drop_off.hints.content',
                                  {
                                    carrier: helpers.getCarrierText(
                                      this.props.carrier
                                    )
                                  }
                                )
                              }}
                            />
                            <a
                              target="_blank"
                              className="buttonTheme-color"
                              style={{
                                color: '#9900A5',
                                paddingLeft: 8,
                                paddingRight: 8
                              }}
                              href={
                                'https://spaceship.crisp.help/zh-hk/article/5aac5l2v6acq57se5ae5lu2-fa6cn0/'
                              }>
                              {this.props.t('transactions.dropOffUrl')}{' '}
                              <FontAwesomeIcon icon="external-link-alt" />
                            </a>
                          </div>
                        </>
                      )}
                    {shipment.fromAddress.country !== 'HK' && (
                      <>
                        <br />
                        <br />
                        <div
                          style={{
                            color: '#2C2C2E',
                            fontSize: '12px',
                            paddingTop: '25px',
                            borderTop: '1px solid #ccc',
                            textAlign: 'left'
                          }}>
                          <img
                            src={Hints}
                            style={{
                              height: 14,
                              marginRight: 2,
                              marginBottom: 5
                            }}
                            alt=""
                          />
                          <span
                            style={{
                              color: '#2C2C2E',
                              fontSize: 14,
                              lineHeight: '16px',
                              fontWeight: 700
                            }}>
                            {this.props.t('transactions.dropoffTitle')}
                          </span>
                          <br />
                          {this.props
                            .t('transactions.dropoff', {
                              carrier: get(
                                shipment,
                                `carrierService.carrierName.${this.props.t(
                                  'key'
                                )}`
                              ),
                              returnObjects: true
                            })
                            .map((y, j) => (
                              <span key={`service_text_${j}`}>
                                {j !== 0 && <br />}
                                <span
                                  style={{ fontSize: 12, lineHeight: '18px' }}>
                                  {y}
                                </span>
                                {j === 2 && (
                                  <span
                                    style={{
                                      color: '#9900A5',
                                      fontWeight: 700,
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => {}}>
                                    {this.props.t('transactions.cs')}{' '}
                                    <FontAwesomeIcon icon="angle-right" />
                                  </span>
                                )}
                              </span>
                            ))}
                        </div>
                      </>
                    )}
                  </WrapperDiv>
                  {transaction.status === 2 ? (
                    <WrapperDiv style={{ padding: '20px 15px 0px' }}>
                      <div
                        style={{
                          paddingBottom: '15px',
                          borderBottom: '1px solid #ccc',
                          fontSize: '20px',
                          fontWeight: 700
                        }}>
                        {this.props.t('transactions.fee')}
                      </div>
                      <div style={{ padding: '20px 0' }}>
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('transactions.total_fee')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'right' }}>
                            {this.pricing.chargedRate?.format()}
                          </Col>
                        </ChargeRow>
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.discount')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'right' }}>
                            -{this.pricing.discount?.format()}
                          </Col>
                        </ChargeRow>
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.adjustment')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'right' }}>
                            {this.pricing.adjustment?.format()}
                          </Col>
                        </ChargeRow>
                        <ChargeRow
                          style={{ fontWeight: 700 }}
                          type="flex"
                          align="bottom">
                          <Col span={18}>{this.props.t('payment.total')}</Col>
                          <Col span={6} style={{ textAlign: 'right' }}>
                            {this.pricing.totalCharges?.format()}
                          </Col>
                        </ChargeRow>
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          paddingTop: '25px',
                          borderTop: '1px solid #ccc'
                        }}>
                        <Row
                          style={{ fontWeight: 700 }}
                          type="flex"
                          align="bottom">
                          <Col span={18}>
                            {this.props.t('transactions.paid_ship')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'right' }}>
                            {this.pricing.totalCharges?.format()}
                          </Col>
                        </Row>
                      </div>
                      <ResponsiveDesktopDiv
                        style={{
                          color: '#2C2C2E',
                          fontSize: '12px',
                          paddingTop: '30px',
                          margin: 'auto',
                          width: '90%',
                          textAlign: 'center'
                        }}>
                        {this.props.t('payment.reminder')}
                      </ResponsiveDesktopDiv>
                      <ResponsiveMobileDiv
                        style={{
                          color: '#2C2C2E',
                          fontSize: '12px',
                          paddingTop: '30px',
                          margin: 'auto',
                          width: '50%',
                          textAlign: 'center'
                        }}>
                        {this.props.t('payment.reminder')}
                      </ResponsiveMobileDiv>
                      <div style={{ paddingTop: 30, margin: '0px -15px' }}>
                        {helpers.getCurrentCountry(true) !== 'HK' && (
                          <InfoBar
                            title={{
                              __html: this.props.t(
                                'payment.charge_with_hkd.title'
                              )
                            }}
                            content={{
                              __html: this.props.t(
                                'payment.charge_with_hkd.description',
                                {
                                  price: this.pricing.HKD?.format()
                                }
                              )
                            }}
                          />
                        )}
                      </div>
                    </WrapperDiv>
                  ) : null}
                  {transaction.status !== 2 ? (
                    <WrapperDiv style={{ padding: '20px 15px 0px' }}>
                      <div
                        style={{
                          paddingBottom: '15px',
                          borderBottom: '1px solid #ccc',
                          fontSize: '20px',
                          fontWeight: 700
                        }}>
                        {this.props.t('payment.estimated_fee')}
                      </div>
                      <div style={{ padding: '20px 0' }}>
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.estimated_shipping_fee')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            {this.pricing.freightCharge?.format()}
                          </Col>
                        </ChargeRow>
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.estimated_fuel_surcharge')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            {this.pricing.fuelSurcharge?.format()}
                          </Col>
                        </ChargeRow>
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.other_fee')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            {this.pricing.otherCharges?.format()}
                          </Col>
                        </ChargeRow>
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.pickup_fee')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            {this.pricing.pickupFee?.format()}
                          </Col>
                        </ChargeRow>
                        {/* {this.pricing.returnFee && <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.return_fee')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            {this.pricing.returnFee?.format()}
                          </Col>
                        </ChargeRow>} */}
                        <ChargeRow type="flex" align="bottom">
                          <Col span={18}>
                            {this.props.t('payment.discount')}
                          </Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            -{this.pricing.discount?.format()}
                          </Col>
                        </ChargeRow>
                        <ChargeRow
                          style={{ fontWeight: 700 }}
                          type="flex"
                          align="bottom">
                          <Col span={18}>{this.props.t('payment.total')}</Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            {(
                              this.pricing.finalTotal ||
                              this.pricing.totalCharges
                            )?.format()}
                          </Col>
                        </ChargeRow>
                      </div>
                      <div
                        style={{
                          color: '#2C2C2E',
                          fontSize: '12px',
                          paddingTop: '25px',
                          borderTop: '1px solid #ccc',
                          textAlign: 'center'
                        }}>
                        {this.props.t('payment.reminder')}
                      </div>
                      <div style={{ paddingTop: 30, margin: '0px -15px' }}>
                        {helpers.getCurrentCountry(true) !== 'HK' && (
                          <InfoBar
                            title={{
                              __html: this.props.t(
                                'payment.charge_with_hkd.title'
                              )
                            }}
                            content={{
                              __html: this.props.t(
                                'payment.charge_with_hkd.description',
                                {
                                  price: this.pricing.HKD?.format()
                                }
                              )
                            }}
                          />
                        )}
                      </div>
                    </WrapperDiv>
                  ) : null}
                </div>
                <WrapperDiv style={{ padding: '20px 15px 30px' }}>
                  <div
                    style={{
                      paddingBottom: '15px',
                      borderBottom: '1px solid #ccc',
                      fontSize: '20px',
                      fontWeight: 700
                    }}>
                    {this.props.t('items.item_detail')}
                  </div>
                  <div style={{ padding: '20px 0' }}>
                    {shipment.customsItems.map((r, i) => {
                      return (
                        <ItemsRow key={i} type="flex" align="middle">
                          <Col span={3} style={{ maxWidth: '60px' }}>
                            <div
                              style={{
                                width: '24px',
                                height: '24px',
                                lineHeight: '22px',
                                border: '1px solid #ccc',
                                borderRadius: '99px',
                                textAlign: 'center',
                                fontSize: '10px',
                                color: '#ccc'
                              }}>
                              {i + 1}
                            </div>
                          </Col>
                          <Col style={{ fontSize: '18px' }}>
                            {keyedCategories[r.category] &&
                              keyedCategories[r.category].icon}
                          </Col>
                          <Col
                            style={{
                              fontSize: '12px',
                              flex: '1 1 auto',
                              paddingLeft: '15px'
                            }}>
                            <span style={{ fontWeight: 700 }}>
                              {r.description}
                            </span>
                            <br />
                            <span style={{ color: '#2C2C2E' }}>
                              ×{r.quantity}
                            </span>
                          </Col>
                          <Col span={6} style={{ textAlign: 'left' }}>
                            {new currency({
                              currency: r.currency,
                              amount: r.value * 100,
                              minor: 2
                            })?.format()}
                          </Col>
                        </ItemsRow>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      paddingTop: '25px',
                      borderTop: '1px solid #ccc'
                    }}>
                    <Row style={{ fontWeight: 700 }} type="flex" align="bottom">
                      <Col span={18}>{this.props.t('payment.total')}</Col>
                      <Col span={6} style={{ textAlign: 'left' }}>
                        {`${this.props.t('payment.box', {
                          amount: shipment.totalBox
                        })}（${shipment.totalWeight}kg)`}
                      </Col>
                    </Row>
                  </div>
                </WrapperDiv>
              </div>
              <div
                // className="actions-btn-group"
                style={{ paddingBottom: '60px', textAlign: 'center' }}>
                <StyledButton
                  ghost
                  type="primary"
                  width={210}
                  disabled={size(get(transaction, 'bulkOrder')) > 0}
                  onClick={this.handleDuplicateClick}
                  loading={this.props.duplicateLoading}>
                  {this.props.t('transactions.btn_duplicate')}
                </StyledButton>
              </div>
            </div>
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = {
  getTransactionsRequest,
  duplicateShipmentRequest,
  getCampaignUserInfoRequest
};

const mapStateToProps = state => {
  return {
    user: state.auth.user,
    authRequestSuccess: state.auth.authRequestSuccess,
    uiLoading: !!state.users.loading,
    transactions: get(state, 'users.transactions.data') || false,
    duplicateShipmentSuccess: state.shipments.duplicateShipmentSuccess || false,
    duplicateLoading: !!state.shipments.loading,
    countries: state.shipments.countries || countries,
    campaignUserExist: state.campaigns.campaignUserExist || false,
    campaignInfo: get(state, 'campaigns') || {}
  };
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(TransactionDetails))
);
